import { DOCUMENT, NgFor, NgIf } from '@angular/common';
import { Component, Inject, Renderer2, ViewEncapsulation } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Brand } from '../../models/brand';
import { Category } from '../../models/category';
import { BrandApiService } from '../../services/brand-api.service';
import { CategoryApiService } from '../../services/category-api.service';
import { SeasonalApiService } from '../../services/seasonal-api.service';
import { Seasonal } from '../../models/seasonal';
import { Title, Meta } from '@angular/platform-browser';
import { PagesApiService } from '../../services/pages-api.service';
import { PageMeta } from '../../models/page-meta';
import { ReplaceMetaDatePipe } from '../../pipes/replace-meta-date.pipe';

@Component({
  selector: 'app-seasonal',
  imports: [NgIf, NgFor, RouterModule, ReplaceMetaDatePipe],
  templateUrl: './seasonal.component.html',
  styleUrl: './seasonal.component.css',
  encapsulation: ViewEncapsulation.None
})
export class SeasonalComponent {
  seasonals: Seasonal[] = [];
  mainCategories: Category[] = [];
  topCategories: Category[] = [];
  topBrands: Brand[] = [];
  today = new Date();
  currentYear: number = 0;
  pendingCalls: number = 0;
  isDataLoaded: boolean = false;
  pagemeta: PageMeta[] = [];
  seasonalMeta: PageMeta = {} as PageMeta;

  constructor(
    private categoryApi: CategoryApiService,
    private seasonalApi: SeasonalApiService,
    private brandApi: BrandApiService,
    private title: Title,
    private meta: Meta,
    private replaceMetaPipe: ReplaceMetaDatePipe,
    private pageMetaApi: PagesApiService,
    @Inject(DOCUMENT) private dom: Document,
    private renderer: Renderer2
  ) {
    this.currentYear = this.today.getFullYear();
  }

  ngOnInit() {
    this.loadAllData();
  }

  loadAllData() {
    this.pendingCalls = 4;
    this.getSeasonalEvents();
    this.getTopCategories();
    this.getTopBrands();
    this.getPageMeta();
  }

  checkIfAllDone() {
    this.pendingCalls--;
    if (this.pendingCalls === 0) {
      this.isDataLoaded = true;
    }
  }

  public loadScript(url: string) {
    const body = <HTMLDivElement>document.body;
    const script = document.createElement('script');
    script.innerHTML = '';
    script.src = url;
    script.async = false;
    script.defer = true;
    body.appendChild(script);
  }

  getSeasonalEvents() {
    this.seasonalApi.getSeasonalEvents().subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.seasonals = data.Data as Seasonal[];
      } else {
      }
      this.checkIfAllDone();
    });
  }

  getTopCategories() {
    this.categoryApi.getTopCategories(1).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.topCategories = data.Data as Category[];
      } else {
      }
      this.checkIfAllDone();
    });
  }

  getTopBrands() {
    this.brandApi.getStores(1, 1, 10).subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.topBrands = data.Data;
      } else {
      }
      this.checkIfAllDone();
    });
  }

  getPageMeta() {
    this.pageMetaApi.getPagesMeta().subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        this.pagemeta = data.Data;
        this.updateMetaData();
      } else if (data.Message === 'No Records Found') {
        this.pagemeta = [];
      } else {
      }
      this.checkIfAllDone();
    });
  }

  updateMetaData() {
    const pageMetaData = this.pagemeta.find(
      (p) => p.PageName === 'seasonal' && p.IsActive
    );
    this.seasonalMeta = pageMetaData as PageMeta;
    if (pageMetaData!.PageId > 0) {
      const pageTitle = this.replaceMetaPipe.transform(pageMetaData!.PageTitle!);
      const pageDescription = this.replaceMetaPipe.transform(pageMetaData!.PageDescription!);
      const pageUrl = `https://www.dealszo.com/seasonal/`;
      // const keyWords = pageMetaData!.PageKeywords!;

      // Update the title and meta tags dynamically
      this.title.setTitle(pageTitle);
      this.meta.updateTag({ name: 'title', content: pageTitle });
      this.meta.updateTag({ name: 'description', content: pageDescription });
      // this.meta.updateTag({
      //   name: 'keywords',
      //   content: keyWords,
      // });

      this.meta.updateTag({
        name: 'robots',
        content:
          'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      });

      // Open Graph meta tags
      this.meta.updateTag({ property: 'og:type', content: 'website' });
      this.meta.updateTag({ property: 'og:site_name', content: 'dealszo.com' });
      this.meta.updateTag({ property: 'og:title', content: pageTitle });
      this.meta.updateTag({
        property: 'og:description',
        content: pageDescription,
      });
      this.meta.updateTag({ property: 'og:url', content: pageUrl });

      // Canonical URL and other necessary meta tags
      let link: HTMLLinkElement = this.dom.querySelector(
        `link[rel='canonical']`
      )!;
      let alternativeLink: HTMLLinkElement = this.dom.querySelector(
        `link[rel='alternate']`
      )!;
      if (link) {
        this.renderer.setAttribute(link, 'href', pageUrl);
      } else {
        link = this.renderer.createElement('link');
        this.renderer.setAttribute(link, 'rel', 'canonical');
        this.renderer.setAttribute(link, 'href', pageUrl);
        this.renderer.appendChild(this.dom.head, link);
      }
      if (alternativeLink) {
        this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
      } else {
        link = this.renderer.createElement('link');
        this.renderer.setAttribute(alternativeLink, 'rel', 'canonical');
        this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
        this.renderer.appendChild(this.dom.head, link);
      }
    }

    if (pageMetaData?.PageSchema && pageMetaData.PageSchema.trim() !== '') {
      const schema = this.replaceMetaPipe.transform(pageMetaData.PageSchema);
      const jsonLdScripts = this.dom.head.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      jsonLdScripts.forEach(script => script.remove());
      const temp = this.dom.createElement('div');
      temp.innerHTML = schema;
      const scripts = temp.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      scripts.forEach((scriptTag, index) => {
        const newScript = this.dom.createElement('script');
        newScript.type = 'application/ld+json';
        newScript.id = `schema-jsonld-${index}`;
        newScript.text = scriptTag.textContent || '';
        this.dom.head.appendChild(newScript);
      });
    }
  }

  directToBrandCouponPage(brand: Brand) {
    if (brand && brand.SeoName) {
      window.location.href = `${brand.SeoName}-coupons/`;
      // this.router.navigate([`${brand.SeoName}-coupons/`], {
      //   state: { name: brand.StoreName, id: brand.StoreId },
      // });
    } else {
      console.error('Brand is undefined or invalid:', brand);
    }
  }
}
